/**
 * Utility functions for detecting and working with directional dimensions.
 *
 * Dimensions can be "directional" meaning they represent either a source or destination
 * in a network flow or test. This file provides helpers to detect and label these dimensions.
 */

import { DEVICE_TAG_DIMENSION_PREFIX } from '../dictionary/v2/compilers/assetTagMeasurements';
import { ASN_DIMENSIONS } from './asnDimensions';

/**
 * Patterns that indicate a source dimension.
 * Order matters - more specific patterns should come first.
 */
const SOURCE_PATTERNS = [
  // Exact prefixes
  /^src_/, // src_agent, src_as, src_eth_mac
  /^i_src_/, // i_src_as_name, i_src_connect_type_name
  /^input_/, // input_port
  // Contains patterns
  /_src_/, // inet_src_addr, l4_src_port
  /_input_/ // i_input_snmp_alias
];

/**
 * Patterns that indicate a destination dimension.
 * Order matters - more specific patterns should come first.
 */
const DESTINATION_PATTERNS = [
  // Exact prefixes
  /^dst_/, // dst_agent, dst_as, dst_eth_mac
  /^i_dst_/, // i_dst_as_name, i_dst_connect_type_name
  /^output_/, // output_port
  // Contains patterns
  /_dst_/, // inet_dst_addr, l4_dst_port
  /_output_/ // i_output_snmp_alias
];

export function isAppProtocolDimension(dimensionName: string): boolean {
  return dimensionName.startsWith('ktappprotocol__') || dimensionName.startsWith('ktsubtype__');
}

/**
 * Check if a dimension name represents a source direction.
 *
 * @param dimensionName - The dimension field name (e.g., 'src_agent', 'inet_src_addr')
 * @returns true if this is a source dimension
 *
 * @example
 * isSourceDimension('src_agent') // true
 * isSourceDimension('inet_src_addr') // true
 * isSourceDimension('IP_src') // true
 * isSourceDimension('input_port') // true
 * isSourceDimension('dst_agent') // false
 */
export function isSourceDimension(dimensionName: string): boolean {
  if (!dimensionName) {
    return false;
  }
  return SOURCE_PATTERNS.some((pattern) => pattern.test(dimensionName));
}

/**
 * Check if a dimension name represents a destination direction.
 *
 * @param dimensionName - The dimension field name (e.g., 'dst_agent', 'inet_dst_addr')
 * @returns true if this is a destination dimension
 *
 * @example
 * isDestinationDimension('dst_agent') // true
 * isDestinationDimension('inet_dst_addr') // true
 * isDestinationDimension('IP_dst') // true
 * isDestinationDimension('output_port') // true
 * isDestinationDimension('src_agent') // false
 */
export function isDestinationDimension(dimensionName: string): boolean {
  if (!dimensionName) {
    return false;
  }
  return DESTINATION_PATTERNS.some((pattern) => pattern.test(dimensionName));
}

/**
 * Check if a dimension is directional (either source or destination).
 *
 * @param dimensionName - The dimension field name
 * @returns true if this is a source or destination dimension
 */
export function isDirectionalDimension(dimensionName: string): boolean {
  return isSourceDimension(dimensionName) || isDestinationDimension(dimensionName);
}

/**
 * Get the direction of a dimension.
 *
 * @param dimensionName - The dimension field name
 * @returns 'source' | 'destination' | null
 */
export function getDimensionDirection(dimensionName: string): 'source' | 'destination' | null {
  if (isSourceDimension(dimensionName)) {
    return 'source';
  }
  if (isDestinationDimension(dimensionName)) {
    return 'destination';
  }
  return null;
}

/**
 * Get a short direction prefix for display purposes.
 *
 * @param dimensionName - The dimension field name
 * @param useFullWord - If true, returns 'Source'/'Destination' instead of 'Src'/'Dst'
 * @returns 'Src' | 'Dst' | 'Source' | 'Destination' | null
 */
export function getDirectionPrefix(
  dimensionName: string,
  useFullWord = false
): 'Src' | 'Dst' | 'Source' | 'Destination' | null {
  if (isSourceDimension(dimensionName)) {
    return useFullWord ? 'Source' : 'Src';
  }
  if (isDestinationDimension(dimensionName)) {
    return useFullWord ? 'Destination' : 'Dst';
  }
  return null;
}

/**
 * Add a direction prefix to a label if the dimension is directional
 * and the label doesn't already contain a direction indicator.
 *
 * @param dimensionName - The dimension field name
 * @param label - The display label for the dimension
 * @param useFullWord - If true, uses 'Source'/'Destination' instead of 'Src'/'Dst'
 * @returns The label with direction prefix if needed
 *
 * @example
 * addDirectionPrefixToLabel('src_agent', 'Agent') // 'Src Agent'
 * addDirectionPrefixToLabel('src_agent', 'Agent', true) // 'Source Agent'
 * addDirectionPrefixToLabel('src_agent', 'Source Agent') // 'Source Agent' (unchanged)
 * addDirectionPrefixToLabel('test_id', 'Test Name') // 'Test Name' (unchanged)
 */
export function addDirectionPrefixToLabel(dimensionName: string, label: string, useFullWord = false): string {
  if (!label || isAppProtocolDimension(dimensionName)) {
    return label;
  }

  const prefix = getDirectionPrefix(dimensionName, useFullWord);
  if (!prefix) {
    return label;
  }

  // Check if label already contains direction info
  const lowerLabel = label.toLowerCase();
  const hasDirectionPrefix =
    lowerLabel.includes('source') ||
    lowerLabel.includes('destination') ||
    lowerLabel.includes('src') ||
    lowerLabel.includes('dst') ||
    lowerLabel.includes('input') ||
    lowerLabel.includes('output');

  if (hasDirectionPrefix) {
    return label;
  }

  return `${prefix} ${label}`;
}

/**
 * Get the UI intent color for a dimension based on its direction.
 * Source dimensions get 'warning' (orange), destination gets 'info' (blue).
 *
 * @param dimensionName - The dimension field name
 * @returns 'warning' | 'info' | 'success'
 */
export function getDimensionIntent(dimensionName: string): 'warning' | 'info' | 'success' {
  if (isSourceDimension(dimensionName)) {
    return 'warning';
  }
  if (isDestinationDimension(dimensionName)) {
    return 'info';
  }
  return 'success';
}

/**
 * Get the smart default operator for a dimension based on its type and naming patterns.
 *
 * @param dimensionName - The dimension field name
 * @param operatorOptions - Available operators for this dimension from dictionary
 * @returns The operator value to use as default
 */
export function getDefaultOperatorForDimension(
  dimensionName: string,
  operatorOptions: Array<{ value: string; label: string }>
): string {
  // BGP dimensions prefer regex for path/community matching
  if (dimensionName.includes('_bgp_aspath') || /_bgp_(\w+_)?community/.test(dimensionName)) {
    return '~';
  }

  // Name/description fields prefer case-insensitive like for user-friendly search
  if (
    dimensionName.includes('as_name') ||
    dimensionName.includes('asn_name') ||
    dimensionName.includes('interface_description') ||
    dimensionName.includes('snmp_alias')
  ) {
    return 'ILIKE';
  }

  // Default to first available operator (usually '=')
  return operatorOptions[0]?.value || '=';
}

export function isPathDimension(dimensionName: string): boolean {
  return dimensionName.includes('aspath');
}

export function splitPathDimensionValue(dimensionName: string, value: string): string[] {
  if (!isPathDimension(dimensionName)) {
    return [value];
  }
  return value.split(/\s+/).filter((hop, idx, hops) => hop !== '' && hop !== hops[idx - 1]);
}

const ASN_LABEL = /^(.{6}).*(\([0-9]+\))$/;

export function shortenDimensionValue(dimensionName: string, value: string): string {
  if (!(ASN_DIMENSIONS as readonly string[]).includes(dimensionName)) {
    return value;
  }
  const match = ASN_LABEL.exec(value);
  return match ? `${match[1]} ${match[2]}` : value;
}

// ---------------------------------------------------------------------------
// Synthetics dimension helpers
// ---------------------------------------------------------------------------

/** Agent-related dimension names used in synthetics queries */
const AGENT_DIMENSIONS = ['agent_id', 'src_agent', 'dst_agent', 'agent'];

/** Test-related dimension names used in synthetics queries */
const TEST_DIMENSIONS = ['test_id', 'test', 'test_name'];

/**
 * Check if dimension is an agent-related dimension.
 * Agent dimensions support equality (=, <>) and contains (ILIKE, NOT ILIKE) operators.
 */
export function isAgentDimension(dimensionName: string): boolean {
  return AGENT_DIMENSIONS.includes(dimensionName) || dimensionName.startsWith('agent_id_');
}

/**
 * Check if dimension is a test-related dimension.
 * Test dimensions support equality (=, <>) and contains (ILIKE, NOT ILIKE) operators.
 */
export function isTestDimension(dimensionName: string): boolean {
  return TEST_DIMENSIONS.includes(dimensionName) || dimensionName.startsWith('test_id_');
}

/**
 * Check if dimension supports contains operator but not regex.
 * Used for dimensions like DNS hostname.
 */
export function isContainsDimension(dimensionName: string): boolean {
  return dimensionName === 'dns_target_hostname' || dimensionName === 'target_hostname' || dimensionName === 'hostname';
}

// ---------------------------------------------------------------------------
// Label pseudo-dimension helpers
// ---------------------------------------------------------------------------

/** Label pseudo-dimension names used for filtering entities by label */
const LABEL_DIMENSIONS = ['ktlabel_syn_agent', 'ktlabel_syn_test', 'i_device_label'];

/** Check if dimension is a label pseudo-dimension */
export function isLabelDimension(dimensionName: string): boolean {
  return LABEL_DIMENSIONS.includes(dimensionName);
}

/** Check if a label dimension targets agents */
export function isAgentLabelDimension(dimensionName: string): boolean {
  return dimensionName === 'ktlabel_syn_agent';
}

/** Check if a label dimension targets tests */
export function isTestLabelDimension(dimensionName: string): boolean {
  return dimensionName === 'ktlabel_syn_test';
}

/** Check if a label dimension targets devices */
export function isDeviceLabelDimension(dimensionName: string): boolean {
  return dimensionName === 'i_device_label';
}

/**
 * Dimension-name prefixes that should not influence measurement specialization.
 * (e.g. dimensions belonging to shared measurements so shouldn't be used to determine which measurement to specialize for)
 */
export const NON_SPECIALIZING_DIMENSION_PREFIXES = [DEVICE_TAG_DIMENSION_PREFIX] as const;

export function isNonSpecializingDimension(dimensionName: string): boolean {
  if (!dimensionName) {
    return false;
  }
  return NON_SPECIALIZING_DIMENSION_PREFIXES.some((prefix) => dimensionName.startsWith(prefix));
}

export function buildBidirectionalKey(srcKey: string, dstKey: string): string | null {
  if (srcKey.includes('src') && dstKey.includes('dst')) {
    return srcKey.replace('src', 'src|dst');
  }
  if (srcKey.includes('input') && dstKey.includes('output')) {
    return srcKey.replace('input', 'input|output');
  }
  if (srcKey.endsWith('_in') && dstKey.endsWith('_out')) {
    return srcKey.replace(/_in$/, '_in|out');
  }
  return null;
}

export function splitBidirectionalKey(biKey: string): { srcVariant: string; dstVariant: string } | null {
  if (biKey.includes('src|dst')) {
    return { srcVariant: biKey.replace('src|dst', 'src'), dstVariant: biKey.replace('src|dst', 'dst') };
  }
  if (biKey.includes('input|output')) {
    return { srcVariant: biKey.replace('input|output', 'input'), dstVariant: biKey.replace('input|output', 'output') };
  }
  if (biKey.includes('_in|out')) {
    return { srcVariant: biKey.replace('_in|out', '_in'), dstVariant: biKey.replace('_in|out', '_out') };
  }
  return null;
}
